import { doc, getDoc, setDoc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { ChangeSummary } from './sessionStorageService';

interface BatchSaveResult {
    success: boolean;
    error?: string;
    savedChanges: ChangeSummary;
}

class BatchSaveService {
    // Build the full path for userData based on collectionPath and docId
    private buildUserDataPath(collectionPath: string | undefined, docId: string, userId: string): string {
        if (collectionPath) {
            // collectionPath is like: /courses/bhagavadGita/children/sararthaVarsini/children/bgSvCh1/children
            // We need to create: courses/bhagavadGita/children/sararthaVarsini/children/bgSvCh1/children/{docId}/userData/{userId}
            const cleanPath = collectionPath.startsWith('/') ? collectionPath.substring(1) : collectionPath;
            return `${cleanPath}/${docId}/userData/${userId}`;
        }
        // Fallback to old structure if no collectionPath provided
        return `courses/${docId}/userData/${userId}`;
    }

    // Execute batch save for all changes
    async executeBatchSave(
        userId: string,
        docId: string,
        collectionPath: string | undefined,
        changes: ChangeSummary
    ): Promise<BatchSaveResult> {
        const now = Timestamp.now();

        try {
            const progressPath = this.buildUserDataPath(collectionPath, docId, userId);
            const progressRef = doc(db, progressPath);

            // Check if document exists to determine if this is create or update
            const existingDoc = await getDoc(progressRef);

            if (existingDoc.exists()) {
                // Document exists - use updateDoc with dot notation for notes
                const updateData: any = {
                    userId: userId,
                    lastAccessed: serverTimestamp()
                };

                // Add completion status change
                if (changes.completionStatus) {
                    updateData.completionStatus = changes.completionStatus.to;
                    updateData.completedAt = changes.completionStatus.to ? serverTimestamp() : null;
                }

                // Add favorite status change
                if (changes.isFavorited) {
                    updateData.isFavorited = changes.isFavorited.to;
                }

                // Add notes change
                if (changes.notes) {
                    if (changes.notes.to) {
                        // Updating existing notes - use dot notation to preserve created timestamp
                        updateData['notes.content'] = changes.notes.to;
                        updateData['notes.lastUpdated'] = now;
                    } else {
                        // Remove notes
                        updateData.notes = null;
                    }
                }

                await updateDoc(progressRef, updateData);
            } else {
                // Document doesn't exist - use setDoc to create with all fields
                const createData: any = {
                    userId: userId,
                    completionStatus: changes.completionStatus?.to || false,
                    completedAt: changes.completionStatus?.to ? serverTimestamp() : null,
                    isFavorited: changes.isFavorited?.to || false,
                    lastAccessed: serverTimestamp()
                };

                // Add notes if provided
                if (changes.notes?.to) {
                    createData.notes = {
                        content: changes.notes.to,
                        created: now,
                        lastUpdated: now
                    };
                }

                await setDoc(progressRef, createData);
            }

            return {
                success: true,
                savedChanges: changes
            };

        } catch (error: any) {
            console.error('Error executing batch save:', error);
            return {
                success: false,
                error: error.message || 'Failed to save changes',
                savedChanges: {}
            };
        }
    }

    // Validate changes before saving
    validateChanges(changes: ChangeSummary): { valid: boolean; error?: string } {
        if (!changes || Object.keys(changes).length === 0) {
            return { valid: false, error: 'No changes to save' };
        }

        // Validate completion status
        if (changes.completionStatus && typeof changes.completionStatus.to !== 'boolean') {
            return { valid: false, error: 'Invalid completion status' };
        }

        // Validate favorite status
        if (changes.isFavorited && typeof changes.isFavorited.to !== 'boolean') {
            return { valid: false, error: 'Invalid favorite status' };
        }

        // Validate notes
        if (changes.notes && typeof changes.notes.to !== 'string') {
            return { valid: false, error: 'Invalid notes content' };
        }

        return { valid: true };
    }

    // Get human-readable error message
    getErrorMessage(error: string): string {
        switch (error) {
            case 'permission-denied':
                return 'You do not have permission to save these changes';
            case 'unavailable':
                return 'Network error. Please check your connection and try again';
            case 'deadline-exceeded':
                return 'Request timed out. Please try again';
            default:
                return `Failed to save changes: ${error}`;
        }
    }
}

const batchSaveService = new BatchSaveService();
export default batchSaveService;
export type { BatchSaveResult }; 